import { useRef, useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { api, formatError } from "@/lib/api";
import { CheckCircle2, Upload, X, FileText, Image as ImageIcon, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const emptyForm = {
  name: "", email: "", phone: "",
  student_name: "", student_class: "",
  message: "",
};

export default function Admissions() {
  const { t } = useLang();
  const [form, setForm] = useState(emptyForm);
  const [studentPhoto, setStudentPhoto] = useState(null);   // {url, filename}
  const [parentPhoto, setParentPhoto] = useState(null);
  const [documents, setDocuments] = useState([]);           // [{url, filename}]
  const [uploadingKey, setUploadingKey] = useState("");     // 'student' | 'parent' | 'docs'
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);             // { student, email } once submitted

  const studentRef = useRef(null);
  const parentRef = useRef(null);
  const docsRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadOne = async (file) => {
    if (file.size > 15 * 1024 * 1024) {
      throw new Error(t.admissions.form.err_file_too_large);
    }
    const data = new FormData();
    data.append("file", file);
    const res = await api.post("/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
    return { url: res.data.url, filename: res.data.filename || file.name };
  };

  const handleSinglePhoto = (slot) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingKey(slot);
    try {
      const uploaded = await uploadOne(file);
      if (slot === "student") setStudentPhoto(uploaded);
      else setParentPhoto(uploaded);
    } catch (err) {
      toast.error(err.message || t.admissions.form.err_upload);
    } finally {
      setUploadingKey("");
      e.target.value = "";
    }
  };

  const handleDocs = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingKey("docs");
    try {
      const uploaded = [];
      for (const f of files) uploaded.push(await uploadOne(f));
      setDocuments((prev) => [...prev, ...uploaded]);
    } catch (err) {
      toast.error(err.message || t.admissions.form.err_upload);
    } finally {
      setUploadingKey("");
      e.target.value = "";
    }
  };

  const removeDoc = (i) => setDocuments((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!studentPhoto) return toast.error(t.admissions.form.err_missing_student_photo);
    if (!parentPhoto) return toast.error(t.admissions.form.err_missing_parent_photo);
    setSending(true);
    try {
      await api.post("/inquiries", {
        type: "admission",
        name: form.name,
        email: form.email,
        phone: form.phone,
        program: form.student_class,
        message: form.message,
        student_name: form.student_name,
        student_class: form.student_class,
        student_photo_url: studentPhoto.url,
        parent_photo_url: parentPhoto.url,
        document_urls: documents.map((d) => d.url),
      });
      toast.success(t.admissions.form.success);
      setSuccess({ student: form.student_name, email: form.email });
      setForm(emptyForm);
      setStudentPhoto(null);
      setParentPhoto(null);
      setDocuments([]);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const field = "w-full h-12 bg-transparent border-b border-icdh-navy/25 focus:border-icdh-red outline-none text-icdh-navy placeholder:text-icdh-navy/40 transition-colors";

  return (
    <section id="admissions" data-testid="admissions-section" className="bg-icdh-surface py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <Reveal><Overline>{t.admissions.overline}</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-icdh-navy">{t.admissions.title}</h2>
            </Reveal>
            <Reveal delay={0.2}><p className="mt-8 text-icdh-navy/75 text-lg max-w-lg">{t.admissions.lead}</p></Reveal>

            <Reveal delay={0.25}>
              <div className="mt-12">
                <p className="text-xs uppercase tracking-[0.25em] text-icdh-red mb-4">{t.admissions.docs_title}</p>
                <ul className="space-y-3" data-testid="admission-docs">
                  {t.admissions.docs.map((d, i) => (
                    <li key={i} className="flex items-start gap-3 text-icdh-navy/85 text-sm">
                      <CheckCircle2 size={18} className="text-icdh-red mt-0.5 flex-shrink-0" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-10 border-l-2 border-icdh-red pl-5" data-testid="late-admission">
                <p className="text-xs uppercase tracking-[0.25em] text-icdh-navy/70 mb-2">{t.admissions.late_title}</p>
                <p className="text-icdh-navy/80 text-sm leading-relaxed">{t.admissions.late_body}</p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="admission-form" className="bg-icdh-white p-7 md:p-10 border border-icdh-navy/10 space-y-6 lg:sticky lg:top-28">
              <p className="text-xs uppercase tracking-[0.25em] text-icdh-red">{t.nav.apply}</p>

              <input required data-testid="adm-name" className={field} placeholder={t.admissions.form.name} value={form.name} onChange={set("name")} />
              <input required type="email" data-testid="adm-email" className={field} placeholder={t.admissions.form.email} value={form.email} onChange={set("email")} />
              <input required data-testid="adm-phone" className={field} placeholder={t.admissions.form.phone} value={form.phone} onChange={set("phone")} />
              <input required data-testid="adm-student-name" className={field} placeholder={t.admissions.form.student_name} value={form.student_name} onChange={set("student_name")} />

              <div>
                <label className="block text-xs uppercase tracking-wider text-icdh-navy/60 mb-1">{t.admissions.form.program}</label>
                <select required data-testid="adm-class" className={`${field} appearance-none bg-icdh-white`} value={form.student_class} onChange={set("student_class")}>
                  <option value="">{t.admissions.form.program_placeholder}</option>
                  {t.admissions.classes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <UploadSlot
                testid="adm-student-photo"
                icon={ImageIcon}
                label={t.admissions.form.student_photo}
                required
                requiredLabel={t.admissions.form.required_photo}
                accept="image/*"
                file={studentPhoto}
                busy={uploadingKey === "student"}
                busyLabel={t.admissions.form.uploading}
                cta={t.admissions.form.upload_cta}
                onPick={() => studentRef.current?.click()}
                onClear={() => setStudentPhoto(null)}
              />
              <input ref={studentRef} type="file" accept="image/*" className="hidden" onChange={handleSinglePhoto("student")} data-testid="adm-student-photo-input" />

              <UploadSlot
                testid="adm-parent-photo"
                icon={ImageIcon}
                label={t.admissions.form.parent_photo}
                required
                requiredLabel={t.admissions.form.required_photo}
                accept="image/*"
                file={parentPhoto}
                busy={uploadingKey === "parent"}
                busyLabel={t.admissions.form.uploading}
                cta={t.admissions.form.upload_cta}
                onPick={() => parentRef.current?.click()}
                onClear={() => setParentPhoto(null)}
              />
              <input ref={parentRef} type="file" accept="image/*" className="hidden" onChange={handleSinglePhoto("parent")} data-testid="adm-parent-photo-input" />

              <div data-testid="adm-docs-slot" className="border border-dashed border-icdh-navy/25 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-icdh-navy">{t.admissions.form.documents}</p>
                    <p className="mt-1 text-xs text-icdh-navy/55">{t.admissions.form.documents_hint}</p>
                  </div>
                  <button type="button" onClick={() => docsRef.current?.click()} data-testid="adm-docs-add"
                    className="h-9 px-3 bg-icdh-navy text-icdh-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 whitespace-nowrap hover:bg-icdh-red transition-colors">
                    <Upload size={14} />{uploadingKey === "docs" ? t.admissions.form.uploading : t.admissions.form.upload_cta_multi}
                  </button>
                </div>
                <input ref={docsRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleDocs} data-testid="adm-docs-input" />
                {documents.length > 0 && (
                  <ul className="mt-4 space-y-2" data-testid="adm-docs-list">
                    {documents.map((d, i) => (
                      <li key={d.url} className="flex items-center gap-2 text-sm bg-icdh-surface px-3 py-2">
                        <FileText size={14} className="text-icdh-red flex-shrink-0" />
                        <span className="truncate flex-1 text-icdh-navy/80">{d.filename}</span>
                        <button type="button" onClick={() => removeDoc(i)} data-testid={`adm-docs-remove-${i}`} className="text-icdh-navy/50 hover:text-icdh-red"><X size={14} /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <textarea data-testid="adm-message" rows={3} className={`${field} h-auto resize-none pt-3`} placeholder={t.admissions.form.message} value={form.message} onChange={set("message")} />

              <button type="submit" disabled={sending} data-testid="adm-submit"
                className="w-full h-14 bg-icdh-red text-icdh-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-navy transition-colors duration-300 disabled:opacity-60">
                {sending ? t.admissions.form.sending : t.admissions.form.submit}
              </button>
            </form>
          </Reveal>
        </div>
      </div>

      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-icdh-navy/80 backdrop-blur-sm flex items-center justify-center p-6"
            data-testid="adm-success-modal"
            onClick={() => setSuccess(null)}
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}
              className="relative bg-icdh-white max-w-lg w-full p-8 md:p-12"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="h-14 w-14 bg-icdh-red flex items-center justify-center">
                <Check className="text-icdh-white" size={28} />
              </div>
              <h3 className="mt-6 font-serif text-3xl md:text-4xl tracking-tight text-icdh-navy">{t.admissions.form.success_title}</h3>
              <p className="mt-4 text-icdh-navy/75 leading-relaxed">
                {t.admissions.form.success_body
                  .replace("{student}", success.student || "")
                  .replace("{email}", success.email || "")}
              </p>
              <button onClick={() => setSuccess(null)} data-testid="adm-success-close"
                className="mt-8 h-12 px-6 bg-icdh-navy text-icdh-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-red transition-colors">
                {t.admissions.form.close}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function UploadSlot({ testid, icon: Icon, label, required, requiredLabel, file, busy, busyLabel, cta, onPick, onClear }) {
  return (
    <div data-testid={testid} className="border border-dashed border-icdh-navy/25 p-4 flex items-center gap-4">
      <div className="h-11 w-11 bg-icdh-surface flex items-center justify-center flex-shrink-0">
        <Icon size={20} className="text-icdh-red" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-icdh-navy flex items-center gap-2">
          {label}
          {required && <span className="text-[10px] uppercase tracking-wider text-icdh-red font-bold">· {requiredLabel}</span>}
        </p>
        {file ? (
          <p className="mt-0.5 text-xs text-icdh-navy/70 truncate" data-testid={`${testid}-filename`}>{file.filename}</p>
        ) : (
          <p className="mt-0.5 text-xs text-icdh-navy/50">JPG · PNG · HEIC · 15 MB</p>
        )}
      </div>
      {file ? (
        <button type="button" onClick={onClear} data-testid={`${testid}-clear`} className="h-9 w-9 border border-icdh-navy/20 hover:bg-icdh-red hover:text-white flex items-center justify-center transition-colors">
          <X size={14} />
        </button>
      ) : (
        <button type="button" onClick={onPick} data-testid={`${testid}-pick`} disabled={busy} className="h-9 px-3 bg-icdh-navy text-icdh-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 whitespace-nowrap hover:bg-icdh-red transition-colors disabled:opacity-60">
          <Upload size={14} />{busy ? busyLabel : cta}
        </button>
      )}
    </div>
  );
}
